import { chartOfAccountsQueryOptions } from "@/features/dashboard/api/queries/journal-accounts-query";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import Select, { type SingleValue } from "react-select";

interface AccountOption {
	value: string;
	label: string;
}

interface JournalAccountSelectProps {
	value: string;
	disabled?: boolean;
	onChange: (code: string, name: string) => void;
}

export function JournalAccountSelect({ value, disabled, onChange }: JournalAccountSelectProps) {
	const { data: accounts = [], isLoading, error } = useQuery(chartOfAccountsQueryOptions());

	const options: AccountOption[] = useMemo(() => {
		// If Books API is not available, provide common accounts as fallback
		if (error || accounts.length === 0) {
			return [
				{ value: "1100", label: "1100 Accounts Receivable - Trade" },
				{ value: "1110", label: "1110 Cash in Bank" },
				{ value: "2030", label: "2030 Output VAT Payable" },
				{ value: "2110", label: "2110 Creditable Withholding Tax" },
				{ value: "4010", label: "4010 Sales Revenue" },
				{ value: "4011", label: "4011 Sales Revenue - VAT Exempt" },
				{ value: "4012", label: "4012 Sales Revenue - Zero Rated" },
				{ value: "5010", label: "5010 Liquidated Damages" },
				{ value: "1120", label: "1120 Retention Receivable" },
				{ value: "5020", label: "5020 Municipal Charge" },
				{ value: "5030", label: "5030 Bank Charges" },
			];
		}
		const filtered = accounts.filter((a) => a.is_active).map((a) => ({ value: a.code, label: `${a.code} ${a.name}` }));
		return filtered;
	}, [accounts, error]);

	// If we have fallback accounts (error case) or loaded accounts, don't treat as loading
	const hasOptions = options.length > 0;

	const selected = value ? (options.find((o) => o.value === value) ?? { value, label: value }) : null;

	return (
		<Select
			classNamePrefix="je-account-select"
			isDisabled={disabled || (isLoading && !hasOptions)}
			value={selected}
			options={options}
			placeholder="Select account..."
			isClearable={false}
			isSearchable
			menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
			styles={{
				menuPortal: (base) => ({ ...base, zIndex: 9999 }),
				control: (base) => ({ ...base, minHeight: 28, fontSize: 13, borderColor: "#e5e7eb", boxShadow: "none" }),
				valueContainer: (base) => ({ ...base, padding: "0 6px" }),
				input: (base) => ({ ...base, margin: 0, padding: 0 }),
				indicatorsContainer: (base) => ({ ...base, padding: 0 }),
				dropdownIndicator: (base) => ({ ...base, padding: "0 4px" }),
				indicatorSeparator: () => ({ display: "none" }),
			}}
			onChange={(opt: SingleValue<AccountOption>) => {
				if (opt) {
					const name = opt.label.replace(opt.value, "").trim();
					onChange(opt.value, name);
				}
			}}
		/>
	);
}
